import { useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { SearchIcon, TextField } from '@citibox-panel/ui';
import type { CarrierOption, LocationMatch } from '@citibox-panel/api-client';
import { useSearchTabs } from '../../search-shell/hooks/useSearchTabs';
import type { LocationFilters } from '../../search-shell/model/shellState';
import { LocationSearchDialog } from './LocationSearchDialog';

export interface AddressSearchProps {
  searchLocations: (q: string) => Promise<LocationMatch[]>;
  fetchLocationCarriers: (locationId: number) => Promise<CarrierOption[]>;
  fetchDeliveryStates: () => Promise<string[]>;
  // Spoken feedback goes to the header host's live region, which — unlike this mode — is mounted
  // whatever the agent is searching by. This slice decides WHAT is said, never where it lands.
  onAnnounce: (message: string) => void;
  // The host focuses the header input on ⌘K / «+» / the empty-state action (shell seam).
  inputRef?: Ref<HTMLInputElement>;
  // Optional control rendered AS the field's end adornment — the host's type trigger. Named as in
  // `PhoneSearch`, so the host hands the same node to either mode.
  trailing?: ReactNode;
}

export function AddressSearch({
  searchLocations,
  fetchLocationCarriers,
  fetchDeliveryStates,
  onAnnounce,
  inputRef,
  trailing,
}: AddressSearchProps) {
  const { openLocation } = useSearchTabs();
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  // A ref and not state, because nothing here may render on its own: the render that matters is
  // the one the exit triggers, and it has to land after MUI has removed the modal.
  const confirmed = useRef<{ location: LocationMatch; filters: LocationFilters } | null>(null);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    setDialogOpen(true);
  };

  // REMEMBERS. Everything a confirm causes happens on the way out — see below — so this handler
  // touches nothing outside itself.
  const handleConfirm = (location: LocationMatch, filters: LocationFilters): void => {
    confirmed.current = { location, filters };
  };

  // ACTS, at the one moment MUI has removed the modal AND stripped `aria-hidden` off the page:
  // the tab created here mounts on a page assistive tech can read, so the list's own live region
  // is born addressable instead of inside a subtree nobody is listening to.
  const handleExited = (): void => {
    const confirm = confirmed.current;
    // A close that confirmed nothing — Escape, «Cancelar», the ✕ — has nothing to replay.
    if (confirm === null) return;
    openLocation(confirm.location.id, confirm.location.address, confirm.filters);
    // Announced rather than focused: the agent who just opened one building is one keystroke from
    // typing the next address, and moving them into the list would cost the flow that step.
    // TODO: i18n
    onAnnounce(`Ubicación abierta: ${confirm.location.address}`);
    // Consumed, so a later cancel-close cannot reopen the building the agent already left.
    confirmed.current = null;
  };

  return (
    <>
      <TextField
        // Names the field for a screen reader and matches its visible placeholder: a name that
        // contradicts the visible text is its own defect, and the modal's field takes another
        // one so a single query never matches both while the dialog is open.
        // TODO: i18n
        aria-label="Buscar por dirección"
        placeholder="Buscar por dirección…"
        value={query}
        onChange={setQuery}
        // Bound to the input and not to the control root: the host's type trigger renders inside
        // this field's end adornment, and activating it is not a way to submit what was typed.
        onKeyDown={handleKeyDown}
        {...(inputRef !== undefined ? { ref: inputRef } : {})}
        variant="search"
        fullWidth
        startAdornment={<SearchIcon fontSize="small" />}
        {...(trailing !== undefined && trailing !== null ? { endAdornment: trailing } : {})}
      />
      <LocationSearchDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
        onExited={handleExited}
        onConfirm={handleConfirm}
        initialQuery={query}
        searchLocations={searchLocations}
        fetchLocationCarriers={fetchLocationCarriers}
        fetchDeliveryStates={fetchDeliveryStates}
      />
    </>
  );
}
